import { cx } from '@emotion/css';
import { Icon, MessageContext } from '@snapchat/snap-design-system-marketing';
import type { FC } from 'react';
import { useContext } from 'react';

import { ShopifyContext } from '../ShopifyProvider';
import { CartContext } from './CartProvider';
import { Price } from './Price';
import {
  atpCss,
  cartItemImg,
  cartItemWrapperCss,
  depositAmountCss,
  depositInfoCss,
  depositLabelCss,
  fullPriceStrikethroughCss,
  priceCss,
  quantityCss,
  quantityModificationCss,
  quantityModificationDisabledCss,
  quantityWrapperCss,
  titleCss,
} from './styles';
import type { SellingPlanDepositData } from './types';

interface CartItemProps {
  productKey: string;
  title: string;
  image: string;
  price: number;
  sku: string;
  optionId: string;
  optionTitle?: string;
  maxQuantity?: number;
  atpMessage?: string | null;
  sellingPlanDepositAmounts?: SellingPlanDepositData;
}

export const CartItem: FC<CartItemProps> = ({
  productKey,
  title,
  image,
  atpMessage,
  price,
  optionId,
  maxQuantity,
  optionTitle,
  sellingPlanDepositAmounts,
}) => {
  const cart = useContext(CartContext);
  const { currencyCode = '' } = useContext(ShopifyContext);
  const { formatMessage } = useContext(MessageContext);

  const quantity = cart.lineItems[optionId] ?? 0;
  const buttonDisabled = maxQuantity === quantity;

  // Deposit amount from product metafield, looked up by current currency.
  const depositAmount =
    sellingPlanDepositAmounts && currencyCode ? sellingPlanDepositAmounts[currencyCode] : null;

  return (
    <div className={cartItemWrapperCss}>
      <div className={cartItemImg(image)} />
      <div className={titleCss}>
        {title}
        {optionTitle && optionTitle !== 'Default Title' ? ` - ${optionTitle}` : ''}
      </div>
      {depositAmount ? (
        <div className={depositInfoCss}>
          <Price className={fullPriceStrikethroughCss} productPrice={price} />
          <Price className={depositAmountCss} productPrice={depositAmount} />
          <div className={depositLabelCss}>
            {formatMessage({ id: 'depositDueToday', defaultMessage: 'Deposit due today' })}
          </div>
        </div>
      ) : (
        <Price className={priceCss} productPrice={price} />
      )}
      <div className={quantityWrapperCss}>
        <button
          onClick={() =>
            cart.removeOne({
              productKey,
              optionId,
              currencyCode,
            })
          }
          className={quantityModificationCss}
        >
          <Icon name="remove" />
        </button>
        <span className={quantityCss}>{quantity}</span>
        <button
          disabled={buttonDisabled}
          onClick={() =>
            cart.addOne({
              productKey,
              optionId,
              currencyCode,
            })
          }
          className={cx(quantityModificationCss, {
            [quantityModificationDisabledCss]: buttonDisabled,
          })}
        >
          <Icon name="add" />
        </button>
      </div>
      {atpMessage && <div className={atpCss}>{atpMessage}</div>}
    </div>
  );
};
